import {
  ReactNode,
  createContext,
  useContext,
  useMemo,
  useState,
  useEffect,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import FormatAlignLeftOutlinedIcon from "@mui/icons-material/FormatAlignLeftOutlined";
import CarpenterOutlinedIcon from "@mui/icons-material/CarpenterOutlined";
import EngineeringOutlinedIcon from "@mui/icons-material/EngineeringOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import BiotechIcon from "@mui/icons-material/Biotech";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import UsersIcon from "@mui/icons-material/PeopleOutlineOutlined";
import ProfileIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import LogIcon from "@mui/icons-material/DescriptionOutlined";
import FormatListNumberedOutlinedIcon from "@mui/icons-material/FormatListNumberedOutlined";
import PublicIcon from "@mui/icons-material/PublicOutlined";
import CellTowerOutlinedIcon from "@mui/icons-material/CellTowerOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import CellTowerIcon from "@mui/icons-material/CellTowerOutlined";
import SellOutlinedIcon from "@mui/icons-material/SellOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import LockPersonOutlinedIcon from "@mui/icons-material/LockPersonOutlined";
import PendingActionsOutlinedIcon from "@mui/icons-material/PendingActionsOutlined";
import AssignmentIndOutlinedIcon from "@mui/icons-material/AssignmentIndOutlined";
import { Tooltip } from "@mui/material";
import { EEntity, EModule } from "../components/table/filter/useTables";
import { useAuth } from "./auth";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import {
  DescriptionOutlined,
  HouseOutlined,
  PrivacyTipOutlined,
  ReceiptOutlined,
  StoreOutlined,
  SupportAgent,
  PsychologyOutlined,
  HistoryOutlined,
} from "@mui/icons-material";
import CurrencyExchangeOutlinedIcon from "@mui/icons-material/CurrencyExchangeOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import DomainVerificationOutlinedIcon from "@mui/icons-material/DomainVerificationOutlined";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import RunningWithErrorsIcon from "@mui/icons-material/RunningWithErrors";
import StraightenOutlinedIcon from "@mui/icons-material/StraightenOutlined";
import PaymentOutlined from "@mui/icons-material/PaymentOutlined";
import CalendarMonthOutlined from "@mui/icons-material/CalendarMonthOutlined";
import TravelExploreOutlinedIcon from "@mui/icons-material/TravelExploreOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import TimelineOutlinedIcon from "@mui/icons-material/TimelineOutlined";
import SchemaIcon from "@mui/icons-material/Schema";

interface IRoute {
  name: string;
  path: string;
  icon: React.ElementType;
  entity: EEntity;
  module: EModule;
  children?: IRoute[];
}
export interface IModule {
  name: string;
  key: EModule;
  icon: React.ElementType;
}
interface ModuleCContextType {
  module: EModule | "";
  modules: IModule[];
  routes: IRoute[];
  changeModule: ({ module }: { module: EModule | "" }) => void;
}

// ----------------- Context -----------------
const ModuleContext = createContext<ModuleCContextType | undefined>(undefined);

export type TModule = "towermanagement" | "global" | "helpDesk" | "financial";

export const ModuleProvider = ({ children }: { children: ReactNode }) => {
  const [module, setModule] = useState<EModule | "">("");
  const [modules] = useState<IModule[]>([
    { name: "Financeiro", key: EModule.FINANCIAL, icon: AccountBalanceIcon },
    {
      name: "Gerenciamento de torres",
      key: EModule.TOWERMANAGEMENT,
      icon: CellTowerOutlinedIcon,
    },
    { name: "Global", key: EModule.GLOBAL, icon: PublicIcon },
    { name: "Help Desk", key: EModule.HELPDESK, icon: SupportAgent },
  ]);
  const [routes, setRoutes] = useState<IRoute[]>([]);
  const { user } = useAuth();

  const changeModule = ({ module }: { module: EModule | "" }) =>
    setModule(module);

  // helper: checa permissão de view
  const hasViewPermission = (
    moduleKey: EModule,
    entity: EEntity,
    profile: any,
  ) => {
    const modKey = moduleKey as unknown as string;
    const entityKey = String(entity).replace(
      `${String(moduleKey).toLowerCase()}_`,
      "",
    );
    return Boolean(profile?.modules?.[modKey]?.[entityKey]?.view);
  };

  // helper recursivo: filtra rotas por permissão (mantém pai se sobrar filho)
  const filterRoutesByPermission = (
    routes: IRoute[],
    moduleKey: EModule,
    profile: any,
  ): IRoute[] =>
    routes
      .map((r) => {
        if (r.children?.length) {
          const filteredChildren = filterRoutesByPermission(
            r.children,
            moduleKey,
            profile,
          );
          return filteredChildren.length
            ? { ...r, children: filteredChildren }
            : null;
        }
        return hasViewPermission(moduleKey, r.entity, profile) ? r : null;
      })
      .filter(Boolean) as IRoute[];

  useEffect(() => {
    if (!user) return;
    const EModuleValue = module;
    if (!EModuleValue) return;

    // --- filhos do agrupamento "Equipamentos"
    const equipamentosChildren: IRoute[] = [
      {
        name: "Equipamentos", // <-- Adicionado aqui para listar na sub-aba
        path: "/towermanagement/equipments",
        icon: Inventory2OutlinedIcon,
        module: EModule.TOWERMANAGEMENT,
        entity: EEntity.TWM_EQUIPMENTS,
      },
      {
        name: "Categorias",
        path: "/towermanagement/equipments/categoryEquipment",
        icon: CarpenterOutlinedIcon,
        module: EModule.TOWERMANAGEMENT,
        entity: EEntity.TWM_CATEGORYEQUIPMENT,
      },
      {
        name: "Modelos",
        path: "/towermanagement/equipments/equipmentModels",
        icon: Inventory2OutlinedIcon,
        module: EModule.TOWERMANAGEMENT,
        entity: EEntity.TWM_EQUIPMENTMODELS,
      },
    ];

    // --- filhos do agrupamento "Cadastros"
    const cadastrosChildren: IRoute[] = [
      {
        name: "Clientes",
        path: "/towermanagement/customers",
        icon: GroupOutlinedIcon,
        module: EModule.TOWERMANAGEMENT,
        entity: EEntity.TWM_CUSTOMERS,
      },
      {
        name: "Status",
        path: "/towermanagement/statuses",
        icon: CheckCircleOutlineOutlinedIcon,
        module: EModule.TOWERMANAGEMENT,
        entity: EEntity.TWM_STATUSES,
      },
      {
        name: "Etapas",
        path: "/towermanagement/stages",
        icon: FormatListNumberedOutlinedIcon,
        module: EModule.TOWERMANAGEMENT,
        entity: EEntity.TWM_STAGES,
      },
      {
        name: "Itens",
        path: "/towermanagement/items",
        icon: Inventory2OutlinedIcon,
        module: EModule.TOWERMANAGEMENT,
        entity: EEntity.TWM_ITEMS,
      },
      {
        name: "Torres",
        path: "/towermanagement/towers",
        icon: CellTowerIcon,
        module: EModule.TOWERMANAGEMENT,
        entity: EEntity.TWM_TOWERS,
      },
      {
        name: "Sites",
        path: "/towermanagement/sites",
        icon: LocationOnOutlinedIcon,
        module: EModule.TOWERMANAGEMENT,
        entity: EEntity.TWM_SITES,
      },
      {
        name: "Patologias",
        path: "/towermanagement/pathologies",
        icon: BiotechIcon,
        module: EModule.TOWERMANAGEMENT,
        entity: EEntity.TWM_PATHOLOGIES,
      },
      {
        name: "Serviços",
        path: "/towermanagement/services",
        icon: EngineeringOutlinedIcon,
        module: EModule.TOWERMANAGEMENT,
        entity: EEntity.TWM_SERVICES,
      },
      {
        name: "Origens",
        path: "/towermanagement/origins",
        icon: TravelExploreOutlinedIcon,
        module: EModule.TOWERMANAGEMENT,
        entity: EEntity.TWM_ORIGINS,
      },
      {
        name: "Equipamentos",
        path: "/towermanagement/equipments",
        icon: Inventory2OutlinedIcon,
        module: EModule.TOWERMANAGEMENT,
        entity: EEntity.TWM_EQUIPMENTS,
        children: equipamentosChildren,
      },
      {
        name: "Terceirizadas",
        path: "/towermanagement/outsourcedes",
        icon: GroupsOutlinedIcon,
        module: EModule.TOWERMANAGEMENT,
        entity: EEntity.TWM_OUTSOURCEDES,
      },
      {
        name: "Sistema EHS",
        path: "/towermanagement/ehs",
        icon: VerifiedUserOutlinedIcon,
        module: EModule.TOWERMANAGEMENT,
        entity: EEntity.TWM_EHS,
      },
      {
        name: "Planos de precificações",
        path: "/towermanagement/pricing_plans",
        icon: SellOutlinedIcon,
        module: EModule.TOWERMANAGEMENT,
        entity: EEntity.TWM_PRICINGPLANS,
      },
      {
        name: "Formulários",
        path: "/towermanagement/forms",
        icon: ListAltOutlinedIcon,
        module: EModule.TOWERMANAGEMENT,
        entity: EEntity.TWM_FORMS,
      },
      {
        name: "Formulários Respondidos",
        path: "/towermanagement/formResponses",
        icon: ListAltOutlinedIcon,
        module: EModule.TOWERMANAGEMENT,
        entity: EEntity.TWM_FORMRESPONSES,
      },
      {
        name: "Unidade de Medida",
        path: "/towermanagement/unitOfMeasures",
        icon: StraightenOutlinedIcon,
        module: EModule.TOWERMANAGEMENT,
        entity: EEntity.TWM_FORMRESPONSES,
      },
      {
        name: "SLAs",
        path: "/towermanagement/slas",
        icon: RunningWithErrorsIcon,
        module: EModule.TOWERMANAGEMENT,
        entity: EEntity.TWM_SLAS,
      },
    ];

    const atividadesChildren: IRoute[] = [
      {
        name: "Atividades",
        path: "/towermanagement/activities",
        icon: FormatAlignLeftOutlinedIcon,
        module: EModule.TOWERMANAGEMENT,
        entity: EEntity.TWM_ACTIVITIES,
      },
      {
        name: "Planejamentos",
        path: "/towermanagement/plannings_maintenances",
        icon: AssessmentOutlinedIcon,
        module: EModule.TOWERMANAGEMENT,
        entity: EEntity.TWM_PLANNINGSMAINTENANCES,
      },
      {
        name: "Atribuições",
        path: "/towermanagement/attributions",
        icon: MapOutlinedIcon,
        module: EModule.TOWERMANAGEMENT,
        entity: EEntity.TWM_MEASUREMENTS,
      },
      {
        name: "Linha do tempo",
        path: "/towermanagement/timeline",
        icon: TimelineOutlinedIcon,
        module: EModule.TOWERMANAGEMENT,
        entity: EEntity.TWM_LOCATIONSHISTORY,
      },
      {
        name: "Acessos",
        path: "/towermanagement/access-requests",
        icon: LockPersonOutlinedIcon,
        module: EModule.TOWERMANAGEMENT,
        entity: EEntity.TWM_ACCESS_REQUESTS,
        children: [
          {
            name: "Solicitação",
            path: "/towermanagement/access-requests/requests",
            icon: ArticleOutlinedIcon,
            module: EModule.TOWERMANAGEMENT,
            entity: EEntity.TWM_ACCESS_REQUESTS,
          },
          {
            name: "Pendente Acesso",
            path: "/towermanagement/access-requests/pending",
            icon: PendingActionsOutlinedIcon,
            module: EModule.TOWERMANAGEMENT,
            entity: EEntity.TWM_ACCESS_REQUESTS,
          },
          {
            name: "Cadastro de Acesso",
            path: "/towermanagement/access-requests/assignments",
            icon: AssignmentIndOutlinedIcon,
            module: EModule.TOWERMANAGEMENT,
            entity: EEntity.TWM_ACCESS_REQUESTS,
          },
        ],
      },
    ];

    const aprovacoesChildren: IRoute[] = [
      {
        name: "Medições",
        path: "/towermanagement/measurements",
        icon: CurrencyExchangeOutlinedIcon,
        module: EModule.TOWERMANAGEMENT,
        entity: EEntity.TWM_MEASUREMENTS,
      },
      {
        name: "Controle Financeiro",
        path: "/towermanagement/measurements/financial-control",
        icon: AccountBalanceWalletOutlinedIcon,
        module: EModule.TOWERMANAGEMENT,
        entity: EEntity.TWM_MEASUREMENTS,
      },
      {
        name: "Etapas",
        path: "/towermanagement/approval_flow_execution_steps",
        icon: FormatListNumberedOutlinedIcon,
        module: EModule.TOWERMANAGEMENT,
        entity: EEntity.TWM_APPROVALFLOWEXECUTIONSTEPS,
      },
      {
        name: "Fluxos de Aprovação",
        path: "/towermanagement/approval-flows",
        icon: SchemaIcon,
        module: EModule.TOWERMANAGEMENT,
        entity: EEntity.TWM_APPROVALFLOWEXECUTIONSTEPS,
      },
    ];
    // --- todas as rotas (com o agrupamento "Cadastros")
    const AllRoutes: IRoute[] = [
      {
        name: "Página Inicial",
        path: "/towermanagement",
        icon: HouseOutlined,
        module: EModule.TOWERMANAGEMENT,
        entity: EEntity.TWM_CUSTOMERS,
      },
      {
        name: "Cadastros",
        path: "/towermanagement/cadastros",
        icon: ListAltOutlinedIcon,
        module: EModule.TOWERMANAGEMENT,
        entity: EEntity.TWM_CUSTOMERS, // pai só agrupa
        children: cadastrosChildren,
      },

      {
        name: "Operacional",
        path: "/towermanagement/operacional",
        icon: ManageAccountsOutlinedIcon,
        module: EModule.TOWERMANAGEMENT,
        entity: EEntity.TWM_ACTIVITIES,
        children: atividadesChildren,
      },
      {
        name: "Aprovações",
        path: "/towermanagement/aprovacoes",
        icon: DomainVerificationOutlinedIcon,
        module: EModule.TOWERMANAGEMENT,
        entity: EEntity.TWM_MEASUREMENTENTRIES,
        children: aprovacoesChildren,
      },

      // ----- GLOBAL
      {
        name: "Usuários",
        path: "/global/users",
        icon: UsersIcon,
        module: EModule.GLOBAL,
        entity: EEntity.GBL_USERS,
      },
      {
        name: "Perfis de Permissões",
        path: "/global/profiles",
        icon: ProfileIcon,
        module: EModule.GLOBAL,
        entity: EEntity.GBL_PROFILES,
      },
      {
        name: "Usuários Terceirizados",
        path: "/global/team_users",
        icon: GroupsOutlinedIcon,
        module: EModule.GLOBAL,
        entity: EEntity.GBL_USERS,
      },
      {
        name: "Auditoria",
        path: "/global/audits",
        icon: LogIcon,
        module: EModule.GLOBAL,
        entity: EEntity.GBL_AUDITS,
      },
      {
        name: "ApiKeys",
        path: "/global/apikeys",
        icon: PrivacyTipOutlined,
        module: EModule.GLOBAL,
        entity: EEntity.GBL_APIKEYS,
      },
      {
        name: "Permissões de Campos",
        path: "/global/field-catalog-permissions",
        icon: ManageAccountsOutlinedIcon,
        module: EModule.GLOBAL,
        entity: EEntity.GBL_FIELD_CATALOG_PERMISSIONS,
      },
      {
        name: "Análises IA de Patologias",
        path: "/global/aiPathologySchedules",
        icon: PsychologyOutlined,
        module: EModule.GLOBAL,
        entity: EEntity.GBL_AIPATHOLOGYSCHEDULES,
      },
      {
        name: "Histórico de Análises",
        path: "/global/aiPathologyAnalyses",
        icon: HistoryOutlined,
        module: EModule.GLOBAL,
        entity: EEntity.GBL_AIPATHOLOGYANALYSES,
      },

      // ----- HELP DESK

      {
        name: "Tickets",
        path: "/helpDesk/tickets",
        icon: SupportAgent,
        module: EModule.HELPDESK,
        entity: EEntity.HD_TICKETS,
      },
      {
        name: "Formulários",
        path: "/helpDesk/forms",
        icon: ListAltOutlinedIcon,
        module: EModule.HELPDESK,
        entity: EEntity.HD_FORMS,
      },

      // ----- FINANCEIRO
      // Não incluir a home financeira, pois é uma feature futura
      // {
      //   name: "Página Inicial",
      //   path: "/financial",
      //   icon: HomeOutlined,
      //   module: EModule.FINANCIAL,
      //   entity: EEntity.FIN_ENTRIES, // JA TEM A ENTITY DA HOME PAGE
      // },
      {
        name: "Lançamentos",
        path: "/financial/entries",
        icon: ReceiptOutlined,
        module: EModule.FINANCIAL,
        entity: EEntity.FIN_ENTRIES,
      },
      {
        name: "Categoria Gerencial",
        path: "/financial/managerialCategory",
        icon: ArticleOutlinedIcon,
        module: EModule.FINANCIAL,
        entity: EEntity.FIN_CATEGORIES,
      },
      {
        name: "Parcelamentos",
        path: "/financial/entry-payment-terms",
        icon: CalendarMonthOutlined,
        module: EModule.FINANCIAL,
        entity: EEntity.FIN_ENTRYPAYMENTTERMS,
      },
      {
        name: "Faturamento",
        path: "/financial/invoices",
        icon: DescriptionOutlined,
        module: EModule.FINANCIAL,
        entity: EEntity.FIN_INVOICES,
      },
      {
        name: "Conciliação de Liberação",
        path: "/financial/release-reconciliation",
        icon: LinkOutlinedIcon,
        module: EModule.FINANCIAL,
        entity: EEntity.FIN_INVOICES,
      },
      {
        name: "Cliente/Fornecedor",
        path: "/financial/customers_suppliers",
        icon: GroupOutlinedIcon,
        module: EModule.FINANCIAL,
        entity: EEntity.FIN_CUSTOMERSSUPPLIERS,
      },
      {
        name: "Contas caixa",
        path: "/financial/cash_accounts",
        icon: StoreOutlined,
        module: EModule.FINANCIAL,
        entity: EEntity.FIN_CASHACCOUNTS,
      },
      {
        name: "Condições de Pagamento",
        path: "/financial/payment-terms",
        icon: PaymentOutlined,
        module: EModule.FINANCIAL,
        entity: EEntity.FIN_PAYMENTTERMS,
      },
      {
        name: "Centros de Custo",
        path: "/financial/cost-centers",
        icon: AccountBalanceWalletOutlinedIcon,
        module: EModule.FINANCIAL,
        entity: EEntity.FIN_COSTCENTERS,
      },
      {
        name: "Dashboards",
        path: "/financial/dashboards",
        icon: DashboardOutlinedIcon,
        module: EModule.FINANCIAL,
        entity: EEntity.FIN_ENTRIES,
      },
      {
        name: "Dashboards",
        path: "/towermanagement/dashboards",
        icon: DashboardOutlinedIcon,
        module: EModule.TOWERMANAGEMENT,
        entity: EEntity.TWM_CUSTOMERS,
      },
    ];

    const routesFiltered = filterRoutesByPermission(
      AllRoutes,
      EModuleValue,
      user.profile,
    );
    setRoutes(routesFiltered);
  }, [module, user]);

  const value = useMemo(
    () => ({ module, routes, changeModule, modules }),
    [module, routes, modules],
  );

  return (
    <ModuleContext.Provider value={value}>{children}</ModuleContext.Provider>
  );
};

export const useModule = (): ModuleCContextType =>
  useContext(ModuleContext) as ModuleCContextType;

// ---------------------------------------------------------------------------
// Sidebar cru (sem MUI) — mantém visual do print
// ---------------------------------------------------------------------------

export const SidebarMenuRaw = () => {
  const { routes } = useModule();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const isActive = (p: string) => pathname.startsWith(p);

  // Verificar recursivamente se algum descendente está ativo
  const hasActiveDescendant = (route: IRoute): boolean => {
    if (isActive(route.path)) return true;
    return (route.children ?? []).some((child) => hasActiveDescendant(child));
  };

  return (
    <nav className="nc-sidebar">
      <ul className="nc-list">
        {routes.map((r) =>
          r.children?.length ? (
            <GroupItemRaw
              route={r}
              anyChildActive={hasActiveDescendant(r)}
              onNavigate={(p) => navigate(p)}
              isActive={isActive}
            />
          ) : (
            <LeafItemRaw
              route={r}
              selected={isActive(r.path)}
              onClick={() => navigate(r.path)}
            />
          ),
        )}
      </ul>
    </nav>
  );
};

const LeafItemRaw = ({
  route,
  selected,
  onClick,
}: {
  route: IRoute;
  selected: boolean;
  onClick: () => void;
}) => {
  const Icon = route.icon;
  return (
    <li>
      <Tooltip title={route.name}>
        <button
          type="button"
          className={`nc-item ${selected ? "selected" : ""}`}
          onClick={onClick}
        >
          <Icon />
          <span>{route.name}</span>
        </button>
      </Tooltip>
    </li>
  );
};

const GroupItemRaw = ({
  route,
  anyChildActive,
  onNavigate,
  isActive,
  isNested = false,
}: {
  route: IRoute;
  anyChildActive: boolean;
  onNavigate: (p: string) => void;
  isActive: (p: string) => boolean;
  isNested?: boolean;
}) => {
  const Icon = route.icon;
  const [open, setOpen] = useState(anyChildActive);

  // Atualizar open quando anyChildActive mudar
  useEffect(() => {
    if (anyChildActive) {
      setOpen(true);
    }
  }, [anyChildActive]);

  // ✅ pai só fica "selected" quando a rota do próprio pai está ativa
  const parentSelected = isActive(route.path);

  // Helper para verificar se algum descendente está ativo
  const hasActiveDescendant = (r: IRoute): boolean => {
    if (isActive(r.path)) return true;
    return (r.children ?? []).some((child) => hasActiveDescendant(child));
  };

  return (
    <li>
      <Tooltip title={route.name}>
        <button
          type="button"
          className={`nc-item ${isNested ? "nc-child" : ""} nc-group ${parentSelected ? "selected" : ""}`}
          onClick={() => setOpen((v) => !v)}
          title={route.name}
        >
          <Icon />
          <span>{route.name}</span>

          <svg
            className={`nc-caret ${open ? "open" : ""}`}
            viewBox="0 0 24 24"
            style={{ marginLeft: "auto" }}
          >
            <path
              d={open ? "M7 14l5-5 5 5" : "M7 10l5 5 5-5"}
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </Tooltip>

      {open && (
        <ul className="nc-children">
          {(route.children ?? []).map((child) => {
            if (child.children?.length) {
              return (
                <GroupItemRaw
                  key={child.path}
                  route={child}
                  anyChildActive={hasActiveDescendant(child)}
                  onNavigate={onNavigate}
                  isActive={isActive}
                  isNested={true}
                />
              );
            }

            const ChildIcon = child.icon;
            const selected = isActive(child.path);
            console.log("teste");
            console.log(child.name);
            return (
              <li key={child.path}>
                <Tooltip title={child.name}>
                  <button
                    type="button"
                    className={`nc-item nc-child ${selected ? "selected" : ""}`}
                    onClick={() => onNavigate(child.path)}
                    title={child.name}
                  >
                    <ChildIcon />
                    <span>{child.name}</span>
                  </button>
                </Tooltip>
              </li>
            );
          })}
        </ul>
      )}
      {!isNested && <div className="nc-divider" />}
    </li>
  );
};
